import { ReadonlyVec3 } from 'gl-matrix';

/** Convert centimeters to millimeters. */
export function cmToMm(cm: number): number {
    return cm * 10;
}

/** Convert millimeters to centimeters. */
export function mmToCm(mm: number): number {
    return mm / 10;
}

/** Patient axis of a value or overlay element (DICOM LPS X/Y/Z). */
export type AxisKey = 'x' | 'y' | 'z';

/**
 * Returns the sign (+1 or -1) of the dominant axis component of a
 * 3D direction vector. For axis-aligned unit vectors
 * (e.g. [0, 0, -1]), this returns the sign of the non-zero
 * component.
 *
 * Used to convert a dot-product projection (planeCoordinates) back
 * to the patient-space coordinate along the through-plane axis.
 *
 * @throws if the direction vector is zero-length.
 */
export function getDominantAxisSign(direction: ReadonlyVec3): 1 | -1 {
    let maxAbsVal = 0;
    let sign: 1 | -1 = 1;
    for (let i = 0; i < 3; i++) {
        const absVal = Math.abs(direction[i]);
        if (absVal > maxAbsVal) {
            maxAbsVal = absVal;
            sign = direction[i] >= 0 ? 1 : -1;
        }
    }
    if (maxAbsVal === 0) {
        throw new Error('getDominantAxisSign: zero-length direction vector');
    }
    return sign;
}

/**
 * Calculate the normalized orientation value for a plane coordinate.
 * Converts planeCoordinates (mm, projected along view direction) to
 * the patient-space coordinate (cm) for display.
 *
 * @param planeCoordinates - dot(intersectionPoint, viewDirection) in mm
 * @param viewDirectionSign - sign of the dominant axis component of the
 *   view direction vector (+1 or -1). This corrects the projection so
 *   that the displayed coordinate matches the patient axis regardless
 *   of whether the view direction is positive or negative along that axis.
 */
export function calculateNormalizedOrientationValue(
    planeCoordinates: number,
    viewDirectionSign: number = -1
): string {
    const patientCoordMm = planeCoordinates * viewDirectionSign;
    return (Math.round(patientCoordMm * 10) / 100).toFixed(2);
}

/**
 * Formats a value already in centimeters as a display string with
 * fixed decimal places.
 * @param valueCm - The value in centimeters.
 * @param decimalPlaces - Number of decimal places (default: 2).
 * @returns Formatted string, e.g. "1.23".
 */
export function formatCm(valueCm: number, decimalPlaces: number = 2): string {
    return valueCm.toFixed(decimalPlaces);
}

/** A cm distance with its unit at display precision (2 dp): "1.23 cm". */
export function formatCmWithUnit(valueCm: number): string {
    return `${formatCm(valueCm)} cm`;
}

/** A cm³ volume at 1 dp with its unit: "12.3 cm³". */
export function formatCm3(valueCm3: number): string {
    return `${valueCm3.toFixed(1)} cm³`;
}

/**
 * A signed cm³ delta with an explicit sign and unit: "+12.3 cm³",
 * "−4.0 cm³", "0.0 cm³". The minus is the U+2212 glyph, not a hyphen.
 */
export function formatSignedCm3(valueCm3: number): string {
    const sign = valueCm3 > 0 ? '+' : valueCm3 < 0 ? '−' : '';
    return `${sign}${Math.abs(valueCm3).toFixed(1)} cm³`;
}

/**
 * A signed cm distance with an explicit sign and unit: "+0.24 cm",
 * "−0.50 cm", "0.00 cm" (2 dp by default, ui_specs distance precision).
 * The minus is the U+2212 glyph, not a hyphen — matching {@link formatSignedCm3}.
 */
export function formatSignedCm(
    valueCm: number,
    decimalPlaces: number = 2
): string {
    const sign = valueCm > 0 ? '+' : valueCm < 0 ? '−' : '';
    return `${sign}${formatCm(Math.abs(valueCm), decimalPlaces)} cm`;
}

/**
 * Format a cm value with no more precision than it carries — trailing zeros
 * trimmed and floating-point noise removed (max 4 dp). Used to echo the
 * clinician's ORIGINAL, finer-than-grid request in a "rounded from …" note,
 * where a fixed-precision format would itself hide the value.
 */
export function formatCmTrimmed(valueCm: number): string {
    return parseFloat(valueCm.toFixed(4)).toString();
}

/**
 * Converts an internal mm value to centimeters and formats it as a
 * display string with fixed decimal places.
 *
 * Use 3 decimal places for voxel-scale distances (pixel spacing,
 * slice thickness) and 2 for macro distances (field of view,
 * physical extent). See docs/ui_specs.md for the full convention.
 *
 * @param valueMm - The value in millimeters (domain units).
 * @param decimalPlaces - Number of decimal places (default: 2).
 * @returns Formatted cm string, e.g. "0.098" or "25.60".
 */
export function formatMmAsCm(
    valueMm: number,
    decimalPlaces: number = 2
): string {
    return formatCm(mmToCm(valueMm), decimalPlaces);
}
